/**
 * Authentication API Service
 *
 * Service layer for all authentication-related API calls.
 * Handles communication with backend /auth endpoints.
 *
 * IMPORTANT: All requests use `credentials: 'include'` so that
 * the HTTP-only session_id cookie is sent automatically by the
 * browser. No manual token handling needed.
 *
 * @module AuthService
 */

import type { User, UserProfile, Role, Permission, ImpersonatorContext } from '../state_management_and_stores/auth.store';

/**
 * API base URL from environment
 */
// VITE_API_URL is the API base path (e.g. "/api" for same-origin unified deploy,
// or "https://api.example.com/api" for split deploys). Same convention as
// src/api/http-client.ts. Default matches the unified deploy where Fastify
// serves /api/* on the same origin as the React app.
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const AUTH_BASE_URL = `${API_BASE_URL}/auth`;

/**
 * Custom error class for auth errors
 */
export class AuthError extends Error {
  constructor(
    message: string,
    public code?: string,
    public statusCode?: number
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * API response types
 */
interface ApiErrorResponse {
  error: string;
  code?: string;
}

/**
 * Friendly fallback copy for known backend auth error codes.
 *
 * The backend already returns human-readable `error` strings, so this map is
 * mostly a safety net: if a response carries a recognized `code` we prefer
 * this curated copy; otherwise we fall back to the server's message, and
 * finally to a generic message. Keeping the mapping here means UI surfaces
 * (signup page, auth context toasts) don't each re-invent error wording.
 */
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  EMAIL_ALREADY_REGISTERED:
    'An account with this email already exists. Try signing in, or reset your password if you’ve forgotten it.',
  PROFILE_CREATION_FAILED:
    'We couldn’t finish setting up your account. Please try again in a moment — if it keeps happening, contact support.',
  USER_CREATION_FAILED: 'We couldn’t create your account. Please try again.',
  WEAK_PASSWORD: 'Please choose a password with at least 8 characters.',
  INVALID_EMAIL: 'That email address doesn’t look right. Please check it and try again.',
  MISSING_FIELDS: 'Please fill in your email and password.',
  TERMS_NOT_ACCEPTED:
    'You must accept the Standard License & Data-Usage Agreement to create an account.',
  SERVER_ERROR: 'Something went wrong on our end. Please try again in a moment.',
};

/**
 * Resolve a user-facing message for an auth error: curated copy for known
 * codes, then the backend's own message, then `fallback`.
 */
export function friendlyAuthErrorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (error instanceof AuthError) {
    if (error.code && AUTH_ERROR_MESSAGES[error.code]) return AUTH_ERROR_MESSAGES[error.code];
    if (error.message && error.message !== 'Request failed') return error.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * Backend returns user identity + profile after signup/login.
 * Tokens are in HTTP-only cookies — NOT in the response body.
 */
interface SignupResponse {
  user: User;
  profile: UserProfile;
  // Backend sets this true when Supabase Auth requires email confirmation
  // (mailer_autoconfirm=false). Frontend should NOT call getCurrentUser
  // in that case — there's no session cookie yet.
  needsEmailConfirmation?: boolean;
  // True when the backend detected an existing unconfirmed account for
  // this email and suppressed a second confirmation send. Used by the
  // success page to remind the user to check their spam folder for the
  // original email instead of expecting a fresh one.
  alreadyExisted?: boolean;
}

interface AuthDataResponse {
  user: User;
  profile: UserProfile;
  role: Role;
  permissions: Permission[];
  // Populated when the active session was minted by an admin impersonating
  // this user. Drives the global ImpersonationBanner.
  impersonator?: ImpersonatorContext | null;
  // True when the user must accept the current Standard License & Data-Usage
  // Agreement before using the app. Drives the global TermsAcceptanceGate.
  termsAcceptanceRequired?: boolean;
}

interface GoogleOAuthResponse {
  url: string;
}

/**
 * Current Standard License & Data-Usage Agreement, as served by the backend.
 * `content` is plain text with blank-line-separated paragraphs.
 */
export interface LegalAgreement {
  version: string;
  title: string;
  effectiveDate: string;
  content: string;
}

/**
 * Map backend auth response to frontend types.
 *
 * Backend role shape:   { role: "admin", roleType: "system", isAdmin: true, ... }
 * Frontend Role shape:  { role_name: "admin", role_type: "system", ... }
 *
 * Backend permissions shape: { permissions: string[], role, roleType } | string[]
 * Frontend Permission shape: { id, perm_name }[]
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
function mapAuthResponse(raw: any): AuthDataResponse {
  // Map role: backend uses camelCase, frontend uses snake_case
  const rawRole = raw.role;
  const role: Role = rawRole ? {
    id: rawRole.id || '',
    role_name: rawRole.role_name || rawRole.role || '',
    role_type: rawRole.role_type || rawRole.roleType || '',
    description: rawRole.description,
    is_default: rawRole.is_default ?? false,
    // Preserve backend flags for admin/ALL-org checks
    isAdmin: rawRole.isAdmin ?? false,
    allOrgAdmin: rawRole.allOrgAdmin ?? false,
  } : {
    id: '',
    role_name: '',
    role_type: '',
    is_default: false,
  };

  // Map permissions: backend may send { permissions: string[] } or string[]
  const rawPerms = raw.permissions;
  let permissions: Permission[];
  if (Array.isArray(rawPerms)) {
    // Already an array — could be string[] or Permission[]
    permissions = rawPerms.map((p: any) =>
      typeof p === 'string' ? { id: p, perm_name: p } : p
    );
  } else if (rawPerms && Array.isArray(rawPerms.permissions)) {
    // Object with { permissions: string[] }
    permissions = rawPerms.permissions.map((p: string) => ({
      id: p,
      perm_name: p,
    }));
  } else {
    permissions = [];
  }

  // Pass through the impersonator block as-is — backend already returns it
  // in the camelCase shape the store expects. Tolerate missing for response
  // shapes that don't carry impersonation data (older endpoints).
  const impersonator: ImpersonatorContext | null = raw.impersonator ?? null;

  return {
    user: raw.user,
    profile: raw.profile,
    role,
    permissions,
    impersonator,
    termsAcceptanceRequired: raw.termsAcceptanceRequired ?? false,
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * Make API request with cookies
 *
 * All requests include `credentials: 'include'` so that
 * HTTP-only cookies are automatically attached by the browser.
 */
async function makeAuthRequest<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const response = await fetch(`${AUTH_BASE_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include', // Send HTTP-only cookies automatically
  });

  const data = await response.json();

  if (!response.ok) {
    const error = data as ApiErrorResponse;
    throw new AuthError(
      error.error || 'Request failed',
      error.code,
      response.status
    );
  }

  return data as T;
}

/**
 * Auth Service — all methods use cookie-based auth
 */
export const authService = {
  /**
   * Register new user
   * Backend sets HTTP-only cookies on success.
   */
  async register(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    acceptTerms: boolean
  ): Promise<SignupResponse> {
    return makeAuthRequest<SignupResponse>('/signup', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        firstName,
        lastName,
        fullName: `${firstName} ${lastName}`,
        // Required: the signup form's "I agree to the Standard License &
        // Data-Usage Agreement" checkbox. The backend rejects signup if false.
        acceptTerms,
      }),
    });
  },

  /**
   * Fetch the current Standard License & Data-Usage Agreement (public — no
   * session needed). Used by the signup checkbox's "read the agreement"
   * dialog and the post-login acceptance gate.
   */
  async getLegalAgreement(): Promise<LegalAgreement> {
    return makeAuthRequest<LegalAgreement>('/legal/agreement', {
      method: 'GET',
    });
  },

  /**
   * Record that the current user has accepted the current agreement version.
   * Returns the updated profile and the (now false) requirement flag.
   */
  async acceptTerms(): Promise<{ profile: UserProfile; termsAcceptanceRequired: boolean }> {
    return makeAuthRequest<{ profile: UserProfile; termsAcceptanceRequired: boolean }>('/accept-terms', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  /**
   * Login user
   * Backend sets HTTP-only session_id cookie on success.
   */
  async login(email: string, password: string): Promise<AuthDataResponse> {
    const raw = await makeAuthRequest<any>('/login', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
      }),
    });
    return mapAuthResponse(raw);
  },

  /**
   * Logout user
   * Backend clears HTTP-only cookies.
   */
  async logout(): Promise<void> {
    try {
      await makeAuthRequest<void>('/logout', {
        method: 'POST',
        body: JSON.stringify({}),
      });
    } catch (error) {
      // Even if logout fails on backend, we clear local state
      console.error('Logout error:', error);
    }
  },

  /**
   * Get current user context (requires valid session_id cookie)
   */
  async getCurrentUser(): Promise<AuthDataResponse> {
    const raw = await makeAuthRequest<any>('/me', {
      method: 'GET',
    });
    return mapAuthResponse(raw);
  },

  /**
   * Get user profile (requires valid session_id cookie)
   */
  async getProfile(): Promise<UserProfile> {
    return makeAuthRequest<UserProfile>('/profile', {
      method: 'GET',
    });
  },

  /**
   * Update user profile
   */
  async updateProfile(
    data: {
      fullName?: string;
      firstName?: string;
      lastName?: string;
      phoneNumber?: string;
      avatarUrl?: string;
    }
  ): Promise<UserProfile> {
    return makeAuthRequest<UserProfile>('/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Request password reset email
   */
  async resetPassword(email: string): Promise<void> {
    return makeAuthRequest<void>('/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  /**
   * Update password (requires valid session_id cookie)
   */
  /**
   * Apply a password reset using recovery tokens from the email link.
   *
   * The user clicks the link in the password reset email → Supabase verifies
   * the token and redirects to /auth/reset-password with access/refresh
   * tokens in the URL hash. The page sends those here along with the new
   * password — we don't need a cookie session for this.
   */
  async recoverPassword(
    accessToken: string,
    refreshToken: string,
    newPassword: string
  ): Promise<void> {
    return makeAuthRequest<void>('/recover-password', {
      method: 'POST',
      body: JSON.stringify({
        access_token: accessToken,
        refresh_token: refreshToken,
        newPassword,
      }),
    });
  },

  async updatePassword(newPassword: string): Promise<void> {
    return makeAuthRequest<void>('/update-password', {
      method: 'PUT',
      body: JSON.stringify({ newPassword }),
    });
  },

  /**
   * Resend verification email
   */
  async resendVerification(email: string): Promise<void> {
    return makeAuthRequest<void>('/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  /**
   * Refresh session using session_id cookie.
   * Backend reads the session_id from the HTTP-only cookie,
   * refreshes the Supabase token server-side, and rotates the session.
   */
  async refreshSession(): Promise<AuthDataResponse> {
    const raw = await makeAuthRequest<any>('/refresh', {
      method: 'POST',
    });
    return mapAuthResponse(raw);
  },

  /**
   * Validate current session — checks if cookies hold a valid session.
   * Returns user data if valid, throws if not.
   */
  async validateSession(): Promise<AuthDataResponse> {
    const raw = await makeAuthRequest<any>('/session', {
      method: 'GET',
    });
    return mapAuthResponse(raw);
  },

  /**
   * Initiate Google OAuth — backend generates the OAuth URL.
   * Frontend should redirect the browser to the returned URL.
   */
  async getGoogleOAuthUrl(): Promise<string> {
    const response = await makeAuthRequest<GoogleOAuthResponse>('/google', {
      method: 'POST',
      body: JSON.stringify({
        redirectTo: `${window.location.origin}/auth/callback`,
      }),
    });
    return response.url;
  },

  /**
   * Validate OAuth callback — exchange OAuth code for session.
   * Backend exchanges the code, creates/updates profile, and sets cookies.
   */
  async validateOAuth(code: string): Promise<AuthDataResponse> {
    const raw = await makeAuthRequest<any>('/validate-oauth', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    return mapAuthResponse(raw);
  },

  /**
   * Exchange OAuth tokens for a server-side session (implicit flow).
   * When Supabase returns tokens in the hash fragment instead of a code,
   * send them to the backend to verify and create a session cookie.
   */
  async exchangeOAuthTokens(
    accessToken: string,
    refreshToken: string
  ): Promise<AuthDataResponse> {
    const raw = await makeAuthRequest<any>('/validate-oauth', {
      method: 'POST',
      body: JSON.stringify({ access_token: accessToken, refresh_token: refreshToken }),
    });
    return mapAuthResponse(raw);
  },
};
